"use client";

// Composer attachments state machine (plan 023) — the block-side port of
// the private `useFileUpload` hook. Same states, same toasts, same
// invariants:
//
//   - one AbortController per in-flight tile; remove/clear aborts it
//   - abort detection covers both the thrown AbortError and the signal
//   - the source owns the server row until finalize and deletes the staged
//     row on failure, so this hook never cleans up half-born rows
//   - send gating reads `isUploading` / `allUploaded` / `hasErrors`
//
// Wire differences vs the private hook: the upload lifecycle is the host's
// (`ComposerUploadsSource.upload`); removing a finalized tile deletes its
// server row through the seam's `delete` (fire-and-forget, same as the
// private hook's delete mutation).
import type { SdkUploadFinalized } from "@bg-bgi/magnus-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { isProcessingFailed } from "../message";
import { attachmentCapacity, type ComposerUploadsSource, validateAttachment } from "./attachments";
export type AttachmentStatus = "pending" | "uploading" | "uploaded" | "error";
export type ComposerAttachment = {
  id: string;
  file: File;
  status: AttachmentStatus;
  /** 0–100 from the source's `onProgress`. The built-in tile shows an
   *  indeterminate spinner instead; this is here for hosts that render a
   *  determinate bar (and for tests to assert against). */
  progress: number;
  error?: string;
  previewUrl?: string;
  /** The finalized row — present only once status is "uploaded". Carries
   *  `url`/`storageKey`/`processingStatus` for the message part and the
   *  processing dot. `processingStatus` updates live while the source's
   *  `watchProcessing` subscription is active. */
  uploaded?: SdkUploadFinalized;
  /** Send gate (ADR-055): false while the source's processing watch
   *  reports non-terminal processing. Undefined when no watch is wired —
   *  no gate, matching the pre-watch behavior. */
  processingTerminal?: boolean;
};
export interface UseAttachmentsResult {
  /** Stage files (paste/drop/picker): validates against the source's
   *  accept list, toasts overflow/rejections, uploads immediately. */
  addFiles: (files: File[] | FileList) => Promise<void>;
  /** True when no staged attachment has a non-terminal processing watch
   *  report (true when no watch is wired). */
  allProcessingSettled: boolean;
  /** True when every staged attachment finalized (false when none). */
  allUploaded: boolean;
  /** Abort everything in flight and clear the strip (post-send). */
  clearFiles: () => void;
  files: ComposerAttachment[];
  hasErrors: boolean;
  isUploading: boolean;
  /** Abort (if in flight) and remove one tile. */
  removeFile: (id: string) => void;
  /** Re-run one errored tile through the source. */
  retryUpload: (id: string) => Promise<void>;
  /** Staged attachments that finalized — the sendable set. */
  uploadedFiles: ComposerAttachment[];
}
function attachmentId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `att-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The composer's attachment staging. Always safe to call: with no source
 * every action is a no-op and the strip stays empty (the composer only
 * renders attach affordances when a source is present anyway).
 */
export function useAttachments(source: ComposerUploadsSource | undefined, options?: {
  /** Fires when a tile's processing watch reports a terminal update. */
  onProcessingSettled?: (uploadId: string, fileName: string, failed: boolean) => void;
}): UseAttachmentsResult {
  const [files, setFiles] = useState<ComposerAttachment[]>([]);
  // Ref-counted, not a boolean: overlapping batches (drop while a paste is
  // still uploading) would otherwise clear the flag when the FIRST batch
  // settles, mid-flight for the second.
  const [uploadingCount, setUploadingCount] = useState(0);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  // Live processing watches, keyed by attachment id. A `null` value means
  // "subscribing, unsubscribe not handed back yet" — a source is allowed to
  // report from inside `watchProcessing`, so key presence (not the value) is
  // what marks a watch live.
  const watchUnsubsRef = useRef<Map<string, (() => void) | null>>(new Map());
  const filesRef = useRef<ComposerAttachment[]>([]);
  filesRef.current = files;
  const sourceRef = useRef(source);
  sourceRef.current = source;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Live processing watch (C10): one subscription per finalized tile while
  // the pipeline runs; the tile's dot updates and the send gate holds until
  // the source reports `terminal`.
  //
  // `fileName` is passed in rather than looked up because neither the state
  // nor `filesRef` is a reliable source here: `filesRef` only catches up on
  // render, and a source that reports synchronously does so before the
  // finalize update has rendered. Reading the name (and the gate transition)
  // from inside the `setFiles` updater is worse still — React defers updaters
  // whenever the hook's queue is non-empty, so a second watch reporting in the
  // same poll tick would read nothing and drop its notification permanently.
  const watchUpload = useCallback((attachmentId: string, uploadId: string, fileName: string) => {
    const active = sourceRef.current;
    if (!active?.watchProcessing || watchUnsubsRef.current.has(attachmentId)) {
      return;
    }
    // Mark the watch live before subscribing (see the ref's note): the
    // callback can run before `watchProcessing` returns.
    watchUnsubsRef.current.set(attachmentId, null);
    let settledDuringSubscribe = false;
    const unsub = active.watchProcessing(uploadId, update => {
      // Absent key ⇒ the watch is done or its tile was removed/cleared. Only
      // a misbehaving source reports after unsubscribe, but the notification
      // below must not fire twice or for a tile that is gone.
      if (!watchUnsubsRef.current.has(attachmentId)) {
        return;
      }
      setFiles(prev => prev.map(f => f.id === attachmentId && f.uploaded ? {
        ...f,
        uploaded: {
          ...f.uploaded,
          processingStatus: update.status
        },
        processingTerminal: update.terminal
      } : f));
      if (!update.terminal) {
        return;
      }
      const stored = watchUnsubsRef.current.get(attachmentId);
      watchUnsubsRef.current.delete(attachmentId);
      if (stored) {
        stored();
      } else {
        settledDuringSubscribe = true;
      }
      const failed = isProcessingFailed({
        richPassFailed: update.richPassFailed,
        status: update.status
      });
      optionsRef.current?.onProcessingSettled?.(uploadId, fileName, failed);
    });
    if (settledDuringSubscribe) {
      unsub();
      return;
    }
    watchUnsubsRef.current.set(attachmentId, unsub);
  }, []);
  const unwatch = useCallback((attachmentId_0: string) => {
    watchUnsubsRef.current.get(attachmentId_0)?.();
    watchUnsubsRef.current.delete(attachmentId_0);
  }, []);

  // Unsubscribe everything on unmount.
  useEffect(() => () => {
    for (const unsub_0 of watchUnsubsRef.current.values()) {
      unsub_0?.();
    }
    watchUnsubsRef.current.clear();
  }, []);

  // A finalized row whose tile is gone would otherwise linger until the
  // stale-staging reaper's 24h sweep. Fire-and-forget, same as the private
  // hook's delete mutation.
  const deleteUploadedRow = useCallback((uploadId_0: string) => {
    sourceRef.current?.delete(uploadId_0).catch(err => {
      console.error(err);
    });
  }, []);
  const uploadSingleFile = useCallback(async (attachment: ComposerAttachment): Promise<void> => {
    const active_0 = sourceRef.current;
    if (!active_0) {
      return;
    }
    const abortController = new AbortController();
    abortControllersRef.current.set(attachment.id, abortController);
    try {
      setFiles(prev_1 => prev_1.map(f_1 => f_1.id === attachment.id ? {
        ...f_1,
        status: "uploading" as const,
        progress: 0
      } : f_1));
      const uploaded = await active_0.upload(attachment.file, percent => setFiles(prev_2 => prev_2.map(f_2 => f_2.id === attachment.id && f_2.status === "uploading" ? {
        ...f_2,
        progress: percent
      } : f_2)), abortController.signal);

      // A remove/clear can land in the window between finalize resolving
      // and this line: the PUT was already done so the abort was a no-op,
      // and `removeFile` saw a tile still in "uploading" with no row id to
      // delete. The tile is gone from state, so nothing else ever will.
      if (abortController.signal.aborted) {
        deleteUploadedRow(uploaded.id);
        return;
      }
      setFiles(prev_3 => prev_3.map(f_3 => f_3.id === attachment.id ? {
        ...f_3,
        status: "uploaded" as const,
        progress: 100,
        uploaded,
        // A non-null finalize-time status means the pipeline has
        // work to do; gate until the watch's first report.
        processingTerminal: active_0.watchProcessing ? uploaded.processingStatus == null : undefined
      } : f_3));
      if (active_0.watchProcessing && uploaded.processingStatus != null) {
        watchUpload(attachment.id, uploaded.id, attachment.file.name);
      }
    } catch (error) {
      // Abort surfaces as a thrown AbortError (or a wrapper whose `cause`
      // is one) — or only as the signal having fired. Cover all three;
      // aborted tiles are removed, never errored.
      const cause = (error as {
        cause?: unknown;
      })?.cause;
      const isAbort = error instanceof Error && error.name === "AbortError" || cause instanceof Error && cause.name === "AbortError" || abortController.signal.aborted;
      if (isAbort) {
        return;
      }
      const errorMessage = error instanceof Error ? error.message : "Upload failed";
      toast.error(`Failed to upload "${attachment.file.name}"`, {
        description: errorMessage
      });
      setFiles(prev_0 => prev_0.map(f_0 => f_0.id === attachment.id ? {
        ...f_0,
        status: "error" as const,
        error: errorMessage
      } : f_0));
    } finally {
      abortControllersRef.current.delete(attachment.id);
    }
  }, [deleteUploadedRow, watchUpload]);
  const addFiles = useCallback(async (newFiles: File[] | FileList) => {
    const active_1 = sourceRef.current;
    if (!active_1) {
      return;
    }
    const fileArray = Array.from(newFiles);
    const availableSlots = attachmentCapacity(filesRef.current.length, active_1.maxFiles);
    if (availableSlots <= 0) {
      toast.error("Maximum files reached", {
        description: `You can only upload ${active_1.maxFiles} files at a time.`
      });
      return;
    }
    if (fileArray.length > availableSlots) {
      toast.warning("Some files were not added", {
        description: `Only ${availableSlots} more file(s) can be added. ${fileArray.length - availableSlots} file(s) were skipped.`
      });
    }
    const filesToAdd = fileArray.slice(0, availableSlots);
    const staged: ComposerAttachment[] = [];
    const rejectedFiles: {
      name: string;
      reason: string;
    }[] = [];
    for (const file of filesToAdd) {
      const validation = validateAttachment(file, active_1.accept);
      if (!validation.valid) {
        rejectedFiles.push({
          name: file.name,
          reason: validation.error
        });
        continue;
      }
      staged.push({
        id: attachmentId(),
        file,
        status: "pending",
        progress: 0,
        previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined
      });
    }
    if (rejectedFiles.length > 0) {
      const first = rejectedFiles[0];
      // `validateAttachment` already opens with the filename, so the
      // single-rejection case must not prepend it again.
      const description = rejectedFiles.length === 1 && first ? first.reason : `${rejectedFiles.length} files could not be added`;
      toast.error("Some files were rejected", {
        description
      });
    }
    if (staged.length === 0) {
      return;
    }
    setFiles(prev_4 => [...prev_4, ...staged]);
    setUploadingCount(n => n + 1);
    try {
      await Promise.all(staged.map(uploadSingleFile));
    } finally {
      setUploadingCount(n => n - 1);
    }
  }, [uploadSingleFile]);
  const removeFile = useCallback((id: string) => {
    abortControllersRef.current.get(id)?.abort();
    abortControllersRef.current.delete(id);
    unwatch(id);
    const file_0 = filesRef.current.find(f_4 => f_4.id === id);
    if (file_0?.previewUrl) {
      URL.revokeObjectURL(file_0.previewUrl);
    }
    const uploadId_1 = file_0?.uploaded?.id;
    if (uploadId_1) {
      deleteUploadedRow(uploadId_1);
    }
    setFiles(prev_5 => prev_5.filter(f_5 => f_5.id !== id));
  }, [deleteUploadedRow, unwatch]);
  const clearFiles = useCallback(() => {
    for (const controller of abortControllersRef.current.values()) {
      controller.abort();
    }
    abortControllersRef.current.clear();
    for (const unsub_1 of watchUnsubsRef.current.values()) {
      unsub_1?.();
    }
    watchUnsubsRef.current.clear();
    for (const file_1 of filesRef.current) {
      if (file_1.previewUrl) {
        URL.revokeObjectURL(file_1.previewUrl);
      }
    }
    // Reset the ref, not just the state: the unmount sweep below reads
    // `filesRef.current`, and an unmount that lands before the next render
    // (a post-send layout swap) would otherwise see the cleared tiles as
    // abandoned and delete the rows the sent message references.
    filesRef.current = [];
    setFiles([]);
  }, []);

  // The composer unmounts mid-staging on chat switch and floating-panel
  // close. Abort what is in flight (the source deletes the staged row it
  // owns), revoke preview object URLs, and delete rows that already
  // finalized — `clearFiles` runs on send, so anything still staged at
  // unmount was abandoned.
  useEffect(() => {
    return () => {
      for (const controller_0 of abortControllersRef.current.values()) {
        controller_0.abort();
      }
      abortControllersRef.current.clear();
      for (const file_2 of filesRef.current) {
        if (file_2.previewUrl) {
          URL.revokeObjectURL(file_2.previewUrl);
        }
        if (file_2.uploaded) {
          deleteUploadedRow(file_2.uploaded.id);
        }
      }
    };
  }, [deleteUploadedRow]);
  const retryUpload = useCallback(async (id_0: string) => {
    const attachment_0 = filesRef.current.find(f_6 => f_6.id === id_0);
    if (attachment_0?.status !== "error") {
      return;
    }
    setFiles(prev_6 => prev_6.map(f_7 => f_7.id === id_0 ? {
      ...f_7,
      error: undefined
    } : f_7));
    setUploadingCount(n_0 => n_0 + 1);
    try {
      await uploadSingleFile(attachment_0);
    } finally {
      setUploadingCount(n => n - 1);
    }
  }, [uploadSingleFile]);
  return {
    files,
    uploadedFiles: files.filter(f_8 => f_8.status === "uploaded"),
    isUploading: uploadingCount > 0,
    hasErrors: files.some(f_9 => f_9.status === "error"),
    allUploaded: files.length > 0 && files.every(f_10 => f_10.status === "uploaded"),
    allProcessingSettled: files.every(f_11 => f_11.processingTerminal !== false),
    addFiles,
    removeFile,
    clearFiles,
    retryUpload
  };
}